import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import WeddingHallNav from '../components/WeddingHallNav';
import { venueGridApi } from '../api';
import { CONTRACT_STATUS_COLORS, EVENT_TYPE_COLORS } from '../constants';

interface Venue {
  weddingVenueIdx: number;
  venueName: string;
  floor: string;
  capacityMin: number;
  capacityMax: number;
  status: string;
}

interface TimeSlot {
  weddingTimeSlotIdx: number;
  weddingVenueIdx: number;
  slotTime: string;
  slotLabel: string;
}

interface Booking {
  whCustomerIdx: number;
  groomName: string;
  brideName: string;
  contactName: string;
  eventType: string;
  eventTime: string;
  contractStatus: string;
  weddingVenueIdx: number;
  guaranteedGuests: number;
  assignedStaff?: { name: string };
}

export default function VenueGrid() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().split('T')[0];
  });
  const [venues, setVenues] = useState<Venue[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      const data = await venueGridApi.grid(selectedDate);
      setVenues(data.venues || []);
      setTimeSlots(data.timeSlots || []);
      setBookings(data.bookings || []);
    } catch (error) {
      console.error('홀 그리드 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchGrid();
  }, [fetchGrid]);

  // 날짜 이동
  const moveDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    const offset = d.getTimezoneOffset() * 60000;
    setSelectedDate(new Date(d.getTime() - offset).toISOString().split('T')[0]);
  };

  const goToday = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    setSelectedDate(new Date(now.getTime() - offset).toISOString().split('T')[0]);
  };

  // 홀별 고유 타임슬롯 추출
  const getVenueTimeSlots = (venueIdx: number) => {
    return timeSlots
      .filter((ts) => ts.weddingVenueIdx === venueIdx)
      .sort((a, b) => a.slotTime.localeCompare(b.slotTime));
  };

  // 특정 홀+시간에 대한 예약 찾기
  const findBooking = (venueIdx: number, slotTime: string) => {
    const normalizedSlot = slotTime.substring(0, 5);
    return bookings.find((b) => {
      const bookingTime = b.eventTime ? b.eventTime.substring(0, 5) : '';
      return b.weddingVenueIdx === venueIdx && bookingTime === normalizedSlot;
    });
  };

  // 전체 유니크 타임슬롯 (모든 홀의 타임슬롯 합집합)
  const allUniqueSlots = Array.from(
    new Map(
      timeSlots
        .sort((a, b) => a.slotTime.localeCompare(b.slotTime))
        .map((ts) => [ts.slotTime.substring(0, 5), ts])
    ).values()
  );

  const getCustomerLabel = (b: Booking) => {
    if (b.eventType === '예식') {
      return [b.groomName, b.brideName].filter(Boolean).join(' & ');
    }
    return b.contactName || '-';
  };

  const dateStr = new Date(selectedDate + 'T00:00:00').toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <WeddingHallNav />

          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">홀 현황</h1>
              <p className="text-sm text-gray-500 mt-1">날짜별 홀 예약 현황을 한눈에 확인합니다</p>
            </div>
          </div>

          {/* 날짜 네비게이션 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => moveDate(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
                </button>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => moveDate(1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
                </button>
                <button
                  onClick={goToday}
                  className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  오늘
                </button>
              </div>
              <div className="text-lg font-semibold text-gray-800">{dateStr}</div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-green-400"></span> 예약가능
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-blue-400"></span> 예약됨
                </span>
              </div>
            </div>
          </div>

          {/* 그리드 */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : venues.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <i className="ri-building-4-line text-5xl text-gray-300 mb-4 block"></i>
              <p className="text-gray-500">등록된 홀이 없습니다. 홀관리에서 홀을 먼저 등록해주세요.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 z-10 min-w-[100px]">
                        시간
                      </th>
                      {venues.map((venue) => (
                        <th
                          key={venue.weddingVenueIdx}
                          className="border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm font-semibold text-gray-700 min-w-[220px]"
                        >
                          <div>{venue.venueName}</div>
                          <div className="text-xs font-normal text-gray-400 mt-0.5">
                            {venue.floor && `${venue.floor} · `}
                            {venue.capacityMin && venue.capacityMax
                              ? `${venue.capacityMin}~${venue.capacityMax}명`
                              : venue.capacityMax
                              ? `최대 ${venue.capacityMax}명`
                              : ''}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allUniqueSlots.map((slot) => {
                      const slotTime = slot.slotTime.substring(0, 5);
                      return (
                        <tr key={slotTime}>
                          <td className="border-b border-r border-gray-200 px-4 py-4 text-sm font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">
                            <div className="font-semibold">{slotTime}</div>
                            <div className="text-xs text-gray-400">
                              {slot.slotLabel || ''}
                            </div>
                          </td>
                          {venues.map((venue) => {
                            const booking = findBooking(venue.weddingVenueIdx, slotTime);
                            const venueHasSlot = getVenueTimeSlots(venue.weddingVenueIdx).some(
                              (ts) => ts.slotTime.substring(0, 5) === slotTime
                            );

                            if (!venueHasSlot) {
                              return (
                                <td
                                  key={venue.weddingVenueIdx}
                                  className="border-b border-r border-gray-200 px-2 py-4 text-center bg-gray-50"
                                >
                                  <span className="text-xs text-gray-300">-</span>
                                </td>
                              );
                            }

                            if (booking) {
                              const statusColor =
                                CONTRACT_STATUS_COLORS[booking.contractStatus] ||
                                'bg-gray-100 text-gray-700';
                              const eventColor =
                                EVENT_TYPE_COLORS[booking.eventType] ||
                                'bg-gray-100 text-gray-700';

                              return (
                                <td
                                  key={venue.weddingVenueIdx}
                                  className="border-b border-r border-gray-200 px-2 py-2 cursor-pointer hover:bg-blue-50 transition-colors"
                                  onClick={() =>
                                    navigate(
                                      `/wedding-hall/customer-detail?id=${booking.whCustomerIdx}`
                                    )
                                  }
                                >
                                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span
                                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${eventColor}`}
                                      >
                                        {booking.eventType}
                                      </span>
                                      <span
                                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColor}`}
                                      >
                                        {booking.contractStatus}
                                      </span>
                                    </div>
                                    <p className="font-bold text-gray-800 text-sm truncate">
                                      {getCustomerLabel(booking)}
                                    </p>
                                    {booking.guaranteedGuests > 0 && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        <i className="ri-group-line mr-1"></i>
                                        보증 {booking.guaranteedGuests}명
                                      </p>
                                    )}
                                    {booking.assignedStaff?.name && (
                                      <p className="text-xs text-gray-400 mt-0.5">
                                        <i className="ri-user-line mr-1"></i>
                                        {booking.assignedStaff.name}
                                      </p>
                                    )}
                                  </div>
                                </td>
                              );
                            }

                            return (
                              <td
                                key={venue.weddingVenueIdx}
                                className="border-b border-r border-gray-200 px-2 py-4 text-center"
                              >
                                <div className="flex items-center justify-center">
                                  <span className="w-2.5 h-2.5 rounded-full bg-green-300"></span>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* 통계 요약 */}
              <div className="border-t border-gray-200 bg-gray-50 px-6 py-3 flex items-center gap-6 text-sm text-gray-600">
                <span>
                  <i className="ri-building-4-line mr-1"></i>
                  홀 {venues.length}개
                </span>
                <span>
                  <i className="ri-calendar-check-line mr-1"></i>
                  예약 {bookings.length}건
                </span>
                <span>
                  <i className="ri-group-line mr-1"></i>총 보증인원{' '}
                  {bookings.reduce((sum, b) => sum + (b.guaranteedGuests || 0), 0)}명
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
